import axios, { AxiosError, AxiosRequestConfig } from 'axios';

const API_BASE =
  (import.meta.env.VITE_PUBLIC_API_URL as string | undefined) ??
  'https://api.dgagentlaunch.com';

const TENANT_LS_KEY = 'dgal:tenantSlug';

/**
 * Resolve the active tenant slug. Order of precedence:
 *   1. ?tenant=<slug> query param (writes through to localStorage)
 *   2. localStorage 'dgal:tenantSlug'
 *   3. <slug>.dgagentlaunch.com subdomain
 */
export function resolveTenantSlug(): string | null {
  if (typeof window === 'undefined') return null;
  const reserved = new Set(['app', 'api', 'admin', 'www']);

  const url = new URL(window.location.href);
  const qp = url.searchParams.get('tenant');
  if (qp) {
    window.localStorage.setItem(TENANT_LS_KEY, qp);
    return qp;
  }

  const ls = window.localStorage.getItem(TENANT_LS_KEY);
  if (ls) return ls;

  const m = window.location.hostname.match(/^([^.]+)\.dgagentlaunch\.com$/);
  if (m && !reserved.has(m[1])) return m[1];

  return null;
}

export function setTenantSlug(slug: string | null) {
  if (typeof window === 'undefined') return;
  if (slug) window.localStorage.setItem(TENANT_LS_KEY, slug);
  else window.localStorage.removeItem(TENANT_LS_KEY);
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

let accessTokenGetter: () => string | null = () => null;
let onAuthFail: () => void = () => {};
let refreshFn: (() => Promise<string | null>) | null = null;

export function configureApi(opts: {
  getAccessToken: () => string | null;
  refresh: () => Promise<string | null>;
  onAuthFail: () => void;
}) {
  accessTokenGetter = opts.getAccessToken;
  refreshFn = opts.refresh;
  onAuthFail = opts.onAuthFail;
}

api.interceptors.request.use((cfg) => {
  cfg.headers = cfg.headers ?? {};
  const token = accessTokenGetter();
  if (token) (cfg.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  const slug = resolveTenantSlug();
  if (slug) (cfg.headers as Record<string, string>)['X-Tenant-Slug'] = slug;
  return cfg;
});

// One-shot auto-refresh on 401.
api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as AxiosRequestConfig & { __retried?: boolean };
    if (
      err.response?.status === 401 &&
      original &&
      !original.__retried &&
      refreshFn &&
      !original.url?.includes('/api/auth/refresh') &&
      !original.url?.includes('/api/auth/login')
    ) {
      original.__retried = true;
      const newTok = await refreshFn();
      if (newTok) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${newTok}`;
        return api.request(original);
      }
      onAuthFail();
    }
    return Promise.reject(err);
  }
);

// 402 subscription_required → bounce the user to /account/billing so they can
// re-subscribe. We don't redirect when the failing request itself was billing
// (which would loop), and we don't redirect if we're already on the billing page.
api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    const status = err.response?.status;
    const data = err.response?.data as { error?: string } | undefined;
    if (status === 402 && data?.error === 'subscription_required' && typeof window !== 'undefined') {
      const isBillingRoute = window.location.pathname.startsWith('/account/billing');
      const reqUrl = (err.config?.url || '') as string;
      const isBillingApi = reqUrl.includes('/api/billing');
      if (!isBillingRoute && !isBillingApi) {
        window.location.href = '/account/billing';
      }
    }
    return Promise.reject(err);
  }
);
